import { Button } from "@/components/ui/buttons/button";
import { useSeed } from "@/features/plausibility/use-seed";

/**
 * One-off seeding of the plausibility tables from the in-code seeds. Preview
 * first: a dry run counts what would be written and shows the current table
 * versions without touching anything. A real run is idempotent, so repeating
 * it is safe, but it bumps every version counter it touches.
 */
export const SeedPanel = () => {
	const seed = useSeed();
	const report = seed.data;

	return (
		<section className="rounded-md border border-secondary p-4">
			<h3 className="text-sm font-semibold text-primary">Seed tables</h3>
			<p className="text-sm text-secondary">
				Write the in-code taxonomy, global priors, and discriminator specs into
				the admin database. Idempotent; a real run bumps the table versions. A
				global prior you have edited is left alone: delete the row and seed
				again to restore the seed value. Classes and features published from the
				Taxonomy page are left alone too.
			</p>
			<div className="mt-3 flex flex-wrap items-center gap-2">
				<Button
					color="secondary"
					isDisabled={seed.isPending}
					onClick={() => seed.mutate({ dryRun: true })}
					size="sm"
					type="button"
				>
					Preview (dry run)
				</Button>
				<Button
					isDisabled={seed.isPending}
					onClick={() => seed.mutate({ dryRun: false })}
					size="sm"
					type="button"
				>
					{seed.isPending ? "Seeding…" : "Seed tables"}
				</Button>
			</div>
			{report && (
				<p className="mt-2 text-sm text-secondary" role="status">
					{report.dryRun ? "Would write" : "Wrote"} {report.taxonomy} taxonomy
					classes, {report.priors} global priors
					{report.priorsSkipped > 0 &&
						` (${report.priorsSkipped} edited by staff and left alone)`}
					, {report.discriminators} discriminator specs
					{(report.taxonomySkipped ?? 0) + (report.discriminatorsSkipped ?? 0) >
						0 &&
						` (${report.taxonomySkipped ?? 0} classes and ${report.discriminatorsSkipped ?? 0} specs published from Taxonomy left alone)`}
					. Versions: taxonomy {report.versions.taxonomy}, priors{" "}
					{report.versions.priors}, discriminators{" "}
					{report.versions.discriminators}, lr {report.versions.lr}.
				</p>
			)}
			{seed.error && (
				<p className="mt-2 text-sm text-error-primary" role="alert">
					{seed.error.message}
				</p>
			)}
		</section>
	);
};
